import { PLACEHOLDER_BOX } from "seneca-common/features/calculation/module/components/MathsInput/consts";

import {
  InputTypes,
  LatexPart,
  PlaceholderLatexInputPart,
  StatementPart,
  TemplatePart,
  TemplatePartTypes,
  TextPart
} from "../types";
import { parseLatexExpr } from "../utils/parseLatexExpr";
import { processStatements } from "../utils/processStatements";

const equalSignPart = {
  type: "text",
  value: "="
} as TextPart;

const UNSUPPORTED_LATEX = ["\\infty", "\\overline"];

/**
 * Due to how `MathJS` handles assignment (`=`), this function is needed so that checking is easier to be done.
 *
 * Another bonus is that long equations with equal symbol within it is split and the user only needs to interact with the relevant part.
 *
 * Sub parts of the equation that have no `\\placeholder` will be turned into `LatexPart`
 * And the one part that does have `\\placeholder` will be an `InputPart`
 */
export default function replaceStatementWithInput({
  scope,
  latexPart,
  statementPart
}: {
  scope: any;
  latexPart: LatexPart;
  statementPart: StatementPart;
}): TemplatePart[] {
  const latexWithPlaceholder = latexPart.original.replace(
    statementPart.original,
    PLACEHOLDER_BOX
  );

  const evaluatedLatexWithPlaceholder = removeTexWrap(
    processStatements(scope, latexWithPlaceholder)
  );

  return evaluatedLatexWithPlaceholder
    .split("=")
    .map(subTemplate => {
      if (!subTemplate.includes(PLACEHOLDER_BOX)) {
        return {
          value: subTemplate,
          type: "latex"
        } as LatexPart;
      }

      const correctLatex = subTemplate.replace(
        PLACEHOLDER_BOX,
        processStatements(scope, statementPart.original)
      );

      if (UNSUPPORTED_LATEX.some(latex => subTemplate.includes(latex))) {
        return {
          type: "latex",
          value: correctLatex
        } as LatexPart;
      }

      return {
        type: TemplatePartTypes.INPUT,
        inputType: InputTypes.PLACEHOLDER_LATEX,
        valueWithPlaceholder: subTemplate,
        correctValue: parseLatexExpr(removeTexWrap(correctLatex))
      } as PlaceholderLatexInputPart;
    }, [] as TemplatePart[])
    .reduce(
      // put equal signs back
      (acc, part, index, arr) =>
        acc.concat(index !== arr.length - 1 ? [part, equalSignPart] : [part]),
      [] as TemplatePart[]
    );
}

function removeTexWrap(text: string) {
  return text.replaceAll("<tex>", "").replaceAll("</tex>", "");
}
